import { AGENT_SECRET_FLAGS } from '../constants'
import type { AgentSecretFlag } from '../constants'

/** True when `scope` is one of the dedicated agent secret flags. */
export function isAgentSecretFlag(scope: string): scope is AgentSecretFlag {
  return (AGENT_SECRET_FLAGS as readonly string[]).includes(scope)
}

/** Exact-match scope check: wildcards never count. */
export function hasExactScope(scopes: readonly string[], scope: string): boolean {
  return scopes.includes(scope)
}

/** Check if scopes include the required permission (supports wildcards) */
export function checkScope(scopes: string[], permission: string): boolean {
  if (!permission.includes(':')) return false
  // Agent secret flags must be granted explicitly; no wildcard implies them.
  if (isAgentSecretFlag(permission)) return hasExactScope(scopes, permission)
  const [reqResource, reqAction] = permission.split(':')
  return scopes.some((s) => {
    if (!s.includes(':')) return false
    const [r, a] = s.split(':')
    // Secrets are sensitive enough that legacy/global wildcards must not opt in
    // existing keys. Callers need `secret:*` or a concrete `secret:action`.
    if (reqResource === 'secret' && r === '*') return false
    return (r === reqResource || r === '*') && (a === reqAction || a === '*')
  })
}

/**
 * Agent secret flag an MCP secret tool call needs on top of its `secret:*` scope,
 * or null when the call only touches masked metadata.
 */
export function requiredAgentSecretFlag(tool: string, args: { reveal?: boolean } = {}): AgentSecretFlag | null {
  switch (tool) {
    case 'secret_reveal':
    case 'secret_inject_env':
      return 'mcp:secret-reveal'
    case 'secret_get':
      return args.reveal ? 'mcp:secret-reveal' : null
    case 'secret_export':
      return 'mcp:secret-export'
    case 'secret_grant_create':
      return 'mcp:secret-grant'
    default:
      return null
  }
}
